import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  Box,
  Typography,
  Divider,
  Chip,
  IconButton,
  Snackbar,
} from "@mui/material";
import { ExpandMore, ChevronRight } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_VI } from "material-react-table/locales/vi";
import contractItemsApi from "../../services/contractItems.service";
import dayjs from "dayjs";
import "dayjs/locale/vi";
import {
  formatAmount,
  formatQuantity,
  formatCurrencyWithUnit,
  formatDate
} from "utils/formatters";
import { AppButton } from "components/common/buttons";

const ContractPaymentModal = ({
  open,
  onClose,
  onSave,
  paymentProfile,
  contractItems = [],
  existingPayments = [],
  allContractPayments = [], // Tất cả thanh toán của hợp đồng từ tất cả các kỳ
  editingPayment = null,
}) => {
  const [formData, setFormData] = useState({
    contractItemId: "",
    paymentQuantity: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  // Helper function để kiểm tra item có children
  const hasChildren = useCallback((item) => {
    return item.hasChildren || (item.children && item.children.length > 0);
  }, []);

  // Helper function để tìm item trong cấu trúc hierarchical
  const findItemById = useCallback((items, itemId) => {
    for (const item of items) {
      if (item.itemId === itemId) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = findItemById(item.children, itemId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  useEffect(() => {
    if (editingPayment) {
      setFormData({
        contractItemId: editingPayment.contractItemId || "",
        paymentQuantity: editingPayment.paymentQuantity || "",
        notes: editingPayment.notes || "",
      });

      const item = findItemById(contractItems, editingPayment.contractItemId);
      setSelectedItem(item);
    } else {
      setFormData({
        contractItemId: "",
        paymentQuantity: "",
        notes: "",
      });
      setSelectedItem(null);
    }
    setErrors({});

    // Auto expand all parent items để hiển thị leaf nodes
    if (contractItems && contractItems.length > 0) {
      const getAllParentIds = (items) => {
        const parentIds = new Set();

        const addParentIds = (items) => {
          items.forEach((item) => {
            if (hasChildren(item)) {
              parentIds.add(item.itemId);
              if (item.children) {
                addParentIds(item.children);
              }
            }
          });
        };

        addParentIds(items);
        return parentIds;
      };

      setExpandedItems(getAllParentIds(contractItems));
    }
  }, [editingPayment, contractItems, open, hasChildren, findItemById]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Update selected item when contractItemId changes
    if (field === "contractItemId") {
      const item = findItemById(contractItems, value);
      setSelectedItem(item);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Check if contractItemId is valid (not empty, not "0", and not null/undefined)
    const contractItemIdValue = String(formData.contractItemId).trim();
    if (
      !contractItemIdValue ||
      contractItemIdValue === "" ||
      contractItemIdValue === "0"
    ) {
      newErrors.contractItemId = "Vui lòng chọn hạng mục";
    }

    if (
      !formData.paymentQuantity ||
      parseFloat(formData.paymentQuantity) <= 0
    ) {
      newErrors.paymentQuantity = "Khối lượng thanh toán phải lớn hơn 0";
    }

    // Check if quantity exceeds available quantity
    if (selectedItem && formData.paymentQuantity) {
      const currentPayments = existingPayments
        .filter(
          (p) =>
            parseInt(p.contractItemId) === parseInt(formData.contractItemId) &&
            (!editingPayment ||
              p.contractPaymentId !== editingPayment.contractPaymentId)
        )
        .reduce((sum, p) => sum + parseFloat(p.paymentQuantity), 0);

      const totalPaymentQuantity =
        currentPayments + parseFloat(formData.paymentQuantity);

      if (totalPaymentQuantity > selectedItem.quantity) {
        newErrors.paymentQuantity = `Tổng khối lượng thanh toán không được vượt quá ${selectedItem.quantity} ${selectedItem.unit}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Ensure contractItemId is a valid number
      const contractItemId = parseInt(formData.contractItemId);
      const paymentQuantity = parseFloat(formData.paymentQuantity);

      // Additional validation
      if (isNaN(contractItemId) || contractItemId <= 0) {
        console.error("Invalid contractItemId:", formData.contractItemId);
        setErrors({ contractItemId: "Vui lòng chọn hạng mục hợp lệ" });
        return;
      }

      if (isNaN(paymentQuantity) || paymentQuantity <= 0) {
        console.error("Invalid paymentQuantity:", formData.paymentQuantity);
        setErrors({ paymentQuantity: "Khối lượng thanh toán không hợp lệ" });
        return;
      }

      // Kiểm tra xem tổng khối lượng thanh toán có vượt quá khối lượng hợp đồng không
      let hasWarning = false;
      const item = findItemById(contractItems, contractItemId);
      if (item && item.contractQuantity) {
        // Tính tổng khối lượng đã thanh toán trước đó
        let totalPreviousPaid = allContractPayments
          .filter((p) => parseInt(p.contractItemId) === contractItemId)
          .reduce((sum, p) => sum + parseFloat(p.paymentQuantity || 0), 0);

        // Nếu đang chỉnh sửa, trừ đi giá trị cũ
        if (editingPayment) {
          totalPreviousPaid -= parseFloat(editingPayment.paymentQuantity || 0);
        }

        // Tính tổng sau khi cộng thêm thanh toán mới
        const totalAfterNewPayment = totalPreviousPaid + paymentQuantity;

        // Kiểm tra có vượt quá không và hiển thị cảnh báo
        if (totalAfterNewPayment > item.contractQuantity) {
          hasWarning = true;
          setSnackbar({
            open: true,
            message: `Bạn đã nhập Tổng khối lượng thanh toán (${totalAfterNewPayment.toLocaleString(
              "vi-VN"
            )} ${
              item.unit
              }) đã vượt quá khối lượng hợp đồng (${item.contractQuantity.toLocaleString(
                "vi-VN"
              )} ${item.unit})`,
            severity: "info",
          });
        }
      }

      // Tính toán paymentAmount (thành tiền trước thuế)
      const paymentAmount = calculateAmount();

      const paymentData = {
        paymentProfileId: paymentProfile.paymentProfileId,
        contractItemId: contractItemId,
        paymentQuantity: paymentQuantity,
        paymentAmount: paymentAmount, // Thêm paymentAmount đã tính toán
        notes: formData.notes.trim() || null,
      };

      await onSave(paymentData, editingPayment?.contractPaymentId);

      // Cập nhật CompletionPercentage của hạng mục sau khi lưu thanh toán thành công
      await updateItemCompletionPercentage(
        contractItemId,
        paymentQuantity,
        !!editingPayment // isEdit = true nếu đang chỉnh sửa
      );

      // Nếu có cảnh báo, delay việc đóng modal để người dùng có thời gian đọc
      if (hasWarning) {
        // Delay 5 giây để người dùng đọc thông báo trước khi đóng modal
        setTimeout(() => {
          onClose();
        }, 5000);
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Error saving contract payment:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      contractItemId: "",
      paymentQuantity: "",
      notes: "",
    });
    setSelectedItem(null);
    setErrors({});
    onClose();
  };

  const getAvailableQuantity = useCallback(
    (itemId) => {
      const item = findItemById(contractItems, itemId);
      if (!item) return 0;



      // Debug: Log all payments for this item
      const relevantPayments = allContractPayments.filter(
        (payment) => parseInt(payment.contractItemId) === parseInt(itemId)
      );


      // Tính tổng đã thanh toán từ TẤT CẢ các kỳ thanh toán
      let paidQuantity = 0;
      let editingPaymentQuantity = 0; // Lưu lại số lượng của thanh toán đang chỉnh sửa

      allContractPayments.forEach((payment) => {
        if (parseInt(payment.contractItemId) === parseInt(itemId)) {
          // Debug: Log payment comparison


          // Nếu đang chỉnh sửa và đây là thanh toán đang chỉnh sửa, lưu lại số lượng nhưng không tính vào tổng
          if (
            editingPayment &&
            payment.contractPaymentId === editingPayment.contractPaymentId
          ) {
            editingPaymentQuantity = parseFloat(payment.paymentQuantity || 0);

            return; // Bỏ qua thanh toán đang chỉnh sửa trong tổng
          }


          paidQuantity += parseFloat(payment.paymentQuantity || 0);
        }
      });

      // Tính số lượng còn lại:
      // - Nếu đang chỉnh sửa: hiển thị số lượng thực sự chưa thanh toán (bao gồm cả thanh toán đang sửa)
      // - Nếu thêm mới: hiển thị số lượng có thể thanh toán thêm
      let available;
      if (editingPayment && editingPaymentQuantity > 0) {
        // Khi chỉnh sửa: hiển thị số còn lại thực tế (18 - 17 = 1)
        available = Math.max(
          0,
          item.contractQuantity - paidQuantity - editingPaymentQuantity
        );

      } else {
        // Khi thêm mới: hiển thị số có thể thanh toán thêm
        available = Math.max(0, item.contractQuantity - paidQuantity);

      }

      return available;
    },
    [contractItems, allContractPayments, editingPayment, findItemById]
  );

  const calculateAmount = () => {
    if (!selectedItem || !formData.paymentQuantity) return 0;

    // Tính thành tiền trước thuế và làm tròn đến hàng đơn vị
    const baseAmount =
      selectedItem.unitPrice * parseFloat(formData.paymentQuantity);

    return Math.round(baseAmount);
  };

  // Hàm tính toán và cập nhật CompletionPercentage của hạng mục
  const updateItemCompletionPercentage = async (
    contractItemId,
    newPaymentQuantity,
    isEdit = false
  ) => {
    try {
      const item = findItemById(contractItems, contractItemId);
      if (!item || !item.contractQuantity || item.contractQuantity <= 0) {
        return;
      }

      // Tính tổng khối lượng đã thanh toán từ TẤT CẢ các kỳ
      let totalPaidQuantity = allContractPayments
        .filter((p) => parseInt(p.contractItemId) === parseInt(contractItemId))
        .reduce((sum, p) => sum + parseFloat(p.paymentQuantity || 0), 0);

      // Nếu đang chỉnh sửa, trừ đi giá trị cũ và cộng giá trị mới
      if (isEdit && editingPayment) {
        totalPaidQuantity =
          totalPaidQuantity - parseFloat(editingPayment.paymentQuantity || 0);
      }

      // Cộng thêm khối lượng thanh toán mới/cập nhật
      totalPaidQuantity += parseFloat(newPaymentQuantity);

      // Tính tỷ lệ phần trăm hoàn thành
      const completionPercentage = Math.min(
        100,
        Math.round((totalPaidQuantity / item.contractQuantity) * 100)
      );

      // Gọi API để cập nhật CompletionPercentage
      await contractItemsApi.updateCompletionPercentage(
        contractItemId,
        completionPercentage
      );
    } catch (error) {
      console.error("Error updating completion percentage:", error);
      // Không throw error để không làm gián đoạn việc lưu thanh toán
    }
  };

  // Helper function để sort items theo itemCode recursively
  const sortItemsByCode = useCallback((items) => {
    const parseItemCode = (code) => {
      if (!code) return [];
      return code.split(".").map((part) => parseInt(part) || 0);
    };

    const compareItemCodes = (a, b) => {
      const codeA = parseItemCode(a.itemCode);
      const codeB = parseItemCode(b.itemCode);

      const maxLength = Math.max(codeA.length, codeB.length);
      for (let i = 0; i < maxLength; i++) {
        const partA = codeA[i] || 0;
        const partB = codeB[i] || 0;
        if (partA !== partB) {
          return partA - partB;
        }
      }
      return 0;
    };

    const sortedItems = items.sort(compareItemCodes);

    // Recursively sort children
    return sortedItems.map((item) => ({
      ...item,
      children: item.children ? sortItemsByCode(item.children) : [],
    }));
  }, []);

  // Helper function để kiểm tra item có phải leaf node không
  const isLeafNode = useCallback(
    (item) => {
      return (
        item.contractQuantity &&
        item.contractQuantity > 0 &&
        item.unitPrice &&
        item.unitPrice > 0 &&
        item.contractAmount &&
        item.contractAmount > 0 &&
        item.unit &&
        item.unit.trim() !== "" &&
        !hasChildren(item)
      );
    },
    [hasChildren]
  );

  // Handle expand/collapse
  const handleToggleExpand = useCallback(
    (itemId) => {
      const newExpanded = new Set(expandedItems);
      if (newExpanded.has(itemId)) {
        newExpanded.delete(itemId);
      } else {
        newExpanded.add(itemId);
      }
      setExpandedItems(newExpanded);
    },
    [expandedItems]
  );

  // Sort contract items once
  const sortedContractItems = useMemo(() => {
    if (!contractItems || contractItems.length === 0) return [];
    return sortItemsByCode(contractItems);
  }, [contractItems, sortItemsByCode]);

  // Prepare data cho tree table (giống ContractItemsTable visibleData)
  const treeTableData = useMemo(() => {
    const result = [];
    const addVisibleItems = (items, level = 0) => {
      items.forEach((item) => {
        result.push({ ...item, level_: level });
        if (
          item.children &&
          item.children.length > 0 &&
          expandedItems.has(item.itemId)
        ) {
          addVisibleItems(item.children, level + 1);
        }
      });
    };
    addVisibleItems(sortedContractItems);
    return result;
  }, [sortedContractItems, expandedItems]);

  // Define columns cho tree table
  const columns = useMemo(
    () => [
      {
        accessorKey: "itemCode",
        header: "Mã hạng mục",
        size: 60,
        enableSorting: false,
        Cell: ({ cell, row }) => {
          const item = row.original;
          const level = item.level_ || 0;
          const indent = level * 20;

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                paddingLeft: `${indent}px`,
              }}
            >
              {hasChildren(item) ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleExpand(item.itemId);
                  }}
                  sx={{ mr: 0.5, p: 0.25 }}
                >
                  {expandedItems.has(item.itemId) ? (
                    <ExpandMore sx={{ fontSize: "1rem" }} />
                  ) : (
                    <ChevronRight sx={{ fontSize: "1rem" }} />
                  )}
                </IconButton>
              ) : (
                <Box sx={{ width: 20, mr: 0.5 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                {item.itemCode}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "itemName",
        header: "Tên hạng mục",
        size: 150,
        enableSorting: false,
        Cell: ({ cell, row }) => {
          const item = row.original;
          return (
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  mb: 0.5,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.3,
                }}
              >
                {item.itemName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: "0.6rem",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {formatQuantity(item.contractQuantity)} {item.unit} ×{" "}
                {formatAmount(item.unitPrice)} ={" "}
                {formatCurrencyWithUnit(item.contractQuantity * item.unitPrice)}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "availableQuantity",
        header: "Khối lượng còn lại",
        size: 50,
        enableSorting: false,
        Cell: ({ row }) => {
          const item = row.original;

          // Chỉ hiển thị số lượng cho leaf nodes
          if (!isLeafNode(item)) {
            return <Box sx={{ textAlign: "right" }}>-</Box>;
          }

          const availableQty = getAvailableQuantity(item.itemId);
          return (
            <Box sx={{ textAlign: "right" }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: availableQty > 0 ? "#4caf50" : "#f44336",
                }}
              >
                {formatQuantity(availableQty)} {item.unit}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.6rem" }}
              >
                Còn lại
              </Typography>
            </Box>
          );
        },
      },
    ],
    [
      hasChildren,
      expandedItems,
      handleToggleExpand,
      getAvailableQuantity,
      isLeafNode,
    ]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            boxShadow: "none",
            border: "1px solid #e0e0e0",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          fontSize: "1.25rem",
          fontWeight: 600,
          fontFamily: "Inter, sans-serif",
        }}
      >
        {editingPayment
          ? "Chỉnh sửa thanh toán hạng mục"
          : "Thêm thanh toán hạng mục"}
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {/* Payment Profile Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "#f8f9fa", borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Thông tin kỳ thanh toán
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Typography variant="body2">
              <strong>Tên:</strong> {paymentProfile.paymentName}
            </Typography>
            <Typography variant="body2">
              <strong>Ngày:</strong>{" "}
              {/* {dayjs(paymentProfile.paymentDate).format("DD/MM/YYYY")} */}
              {formatDate(paymentProfile.paymentDate)}
            </Typography>
            <Chip
              label={`VAT: ${paymentProfile.vat}%`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Cột trái: Bảng chọn hạng mục */}
          <Grid size={{ xs: 12, md: 9 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Chọn hạng mục thanh toán
              </Typography>

              {/* Tree Table hiển thị danh sách hạng mục */}
              {treeTableData.length === 0 ? (
                <Box
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 3,
                    textAlign: "center",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Không có hạng mục nào có thể thanh toán
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontFamily: "Inter, sans-serif",
                      mt: 1,
                      display: "block",
                    }}
                  >
                    💡 Chỉ các hạng mục có đầy đủ thông tin thực thi mới có thể
                    thanh toán
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    maxHeight: 500,
                    overflow: "hidden",
                  }}
                >
                  <MaterialReactTable
                    columns={columns}
                    data={treeTableData}
                    enableColumnOrdering={false}
                    enableColumnPinning={false}
                    enableRowSelection={false}
                    enableGlobalFilter={false}
                    enableFilters={false}
                    enableTopToolbar={false}
                    enableBottomToolbar={false}
                    enablePagination={false}
                    enableSorting={false}
                    enableColumnActions={false}
                    enableColumnDragging={false}
                    enableDensityToggle={false}
                    enableFullScreenToggle={false}
                    enableHiding={false}
                    initialState={{
                      density: "compact",
                    }}
                    muiTableContainerProps={{
                      sx: {
                        border: "none",
                        borderRadius: 0,
                        maxHeight: "500px",
                        boxShadow: "none",
                        overflowY: "auto",
                      },
                    }}
                    muiTableProps={{
                      sx: {
                        fontFamily: "Inter, sans-serif",
                        boxShadow: "none",
                      },
                    }}
                    muiTablePaperProps={{
                      sx: {
                        boxShadow: "none",
                        border: "none",
                      },
                    }}
                    muiTableHeadCellProps={{
                      sx: {
                        fontFamily: "Inter, sans-serif",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        backgroundColor: "#f8f9fa",
                        borderBottom: "1px solid #e0e0e0",
                        borderRight: "1px solid #e0e0e0",
                        boxShadow: "none",
                        py: 1,
                      },
                    }}
                    muiTableBodyCellProps={{
                      sx: {
                        fontFamily: "Inter, sans-serif",
                        fontSize: "0.75rem",
                        borderBottom: "1px solid #f0f0f0",
                        borderRight: "1px solid #f0f0f0",
                        boxShadow: "none",
                        py: 1,
                        verticalAlign: "top",
                        minHeight: "auto",
                        height: "auto",
                      },
                    }}
                    muiTableBodyRowProps={({ row }) => {
                      const item = row.original;
                      const isSelected =
                        parseInt(formData.contractItemId) ===
                        parseInt(item.itemId);
                      const isDisabled = getAvailableQuantity(item.itemId) <= 0;
                      const canSelect = isLeafNode(item) && !isDisabled;

                      return {
                        onClick: () => {
                          if (canSelect) {
                            handleChange("contractItemId", item.itemId);
                          }
                        },
                        sx: {
                          backgroundColor: isSelected ? "#e3f2fd" : "#ffffff",
                          cursor: canSelect ? "pointer" : "default",
                          opacity: isDisabled ? 0.6 : 1,
                          "&:hover": {
                            backgroundColor: isSelected
                              ? "#e3f2fd"
                              : canSelect
                                ? "#f5f5f5"
                                : "#ffffff",
                          },
                          boxShadow: "none",
                        },
                      };
                    }}
                    localization={MRT_Localization_VI}
                  />
                </Box>
              )}

              {/* Error message cho việc chọn hạng mục */}
              {errors.contractItemId && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "block" }}
                >
                  {errors.contractItemId}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Cột phải: Form nhập thông tin thanh toán */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Nhập thông tin thanh toán
              </Typography>
              {/* Hiển thị hạng mục đã chọn */}
              {selectedItem ? (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#e3f2fd",
                    borderRadius: 1,
                    border: "1px solid #2196f3",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Hạng mục đã chọn:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{selectedItem.itemCode}</strong> -{" "}
                    {selectedItem.itemName}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Khối lượng hợp đồng:{" "}
                    {formatQuantity(selectedItem.contractQuantity)}{" "}
                    {selectedItem.unit}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Đơn giá: {formatCurrencyWithUnit(selectedItem.unitPrice)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ fontWeight: 600 }}
                    display="block"
                  >
                    Khối lượng còn lại:{" "}
                    {formatQuantity(getAvailableQuantity(selectedItem.itemId))}{" "}
                    {selectedItem.unit}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    border: "1px dashed #ccc",
                    borderRadius: 1,
                    textAlign: "center",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Chọn hạng mục từ danh sách bên trái để bắt đầu
                  </Typography>
                </Box>
              )}

              {/* Form fields */}
              <TextField
                fullWidth
                label="Khối lượng thanh toán"
                type="number"
                value={formData.paymentQuantity}
                onChange={(e) =>
                  handleChange("paymentQuantity", e.target.value)
                }
                error={!!errors.paymentQuantity}
                helperText={
                  errors.paymentQuantity ||
                  (selectedItem ? `Đơn vị: ${selectedItem.unit}` : "")
                }
                required
                disabled={!selectedItem}
                inputProps={{ min: 0, step: 0.0001 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#e0e0e0" },
                    "&:hover fieldset": { borderColor: "#b0b0b0" },
                  },
                  marginTop: 1,
                }}
              />

              <TextField
                fullWidth
                label="Thành tiền (trước thuế)"
                value={formatAmount(calculateAmount())}
                InputProps={{ readOnly: true }}
                disabled={!selectedItem}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f8f9fa",
                    "& fieldset": { borderColor: "#e0e0e0" },
                    "&:hover fieldset": { borderColor: "#b0b0b0" },
                  },
                  marginTop: 1,
                }}
              />

              <TextField
                fullWidth
                label="Ghi chú"
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                multiline
                rows={4}
                placeholder="Ghi chú về thanh toán này..."
                disabled={!selectedItem}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#e0e0e0" },
                    "&:hover fieldset": { borderColor: "#b0b0b0" },
                  },
                  marginTop: 1,
                }}
              />
            </Box>
          </Grid>
        </Grid>

        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Vui lòng kiểm tra lại thông tin đã nhập
          </Alert>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <AppButton
          onClick={handleClose}
          variant="outlined"
          disabled={loading || snackbar.open} // Disable khi đang loading hoặc có thông báo
        >
          Hủy
        </AppButton>
        <AppButton
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || snackbar.open} // Disable khi đang loading hoặc có thông báo
        >
          {loading ? "Đang lưu..." : editingPayment ? "Cập nhật" : "Thêm mới"}
        </AppButton>
      </DialogActions>

      {/* Snackbar cho thông báo cảnh báo */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={10000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%", fontFamily: "Inter, sans-serif" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ContractPaymentModal;
